import { useEffect } from "react";
import { useDispatch, useStore } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getBookmarks,
  addBookmarkApi,
  removeBookmarkApi,
} from "../services/apiService";
import * as types from "../Redux/bookmarks/bookmark.actionTypes";
import { setHospitalsDetails } from "../Redux/app/app.actions";
import { formatTextToCapitalize } from "../utils";

const liveUrl = process.env.REACT_APP_LIVE_URL;

export const BOOKMARKS_QUERY_KEY = ["bookmarks"];

const toBookmarkListItem = (ele) => ({
  name: formatTextToCapitalize(ele.hosp10_name),
  address: formatTextToCapitalize(`${ele.state}, ${ele.city}`),
  link: `${liveUrl}/hospitals/${ele.provider_number}`,
  provider_number: ele.provider_number,
});

// Fetches bookmarks via React Query and mirrors them into the Redux bookmarks
// slice, so the existing consumers (Header, Popup, CustomTable,
// HospitalListCard, FactSheetTopBanner) keep reading from Redux unchanged.
export const useBookmarks = () => {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: BOOKMARKS_QUERY_KEY,
    queryFn: ({ signal }) => getBookmarks(signal),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!query.data) return;
    const bookmarks = query.data?.data || [];
    const filteredData = bookmarks.filter((ele) => ele.hosp10_name);
    dispatch({
      type: types.FETCH_BOOKMARKS_SUCCESS,
      payload: {
        bookmarkList: filteredData.map(toBookmarkListItem),
        rawBookmarksData: filteredData,
      },
    });
  }, [query.data, dispatch]);

  return query;
};

// Returns an async fn matching the old thunk contract: resolves to
// { success, message } and mirrors the result into Redux + invalidates cache.
export const useAddBookmark = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: (providerNumbers) => addBookmarkApi(providerNumbers),
  });

  return async (providerNumbersInput = []) => {
    const providerNumbers = Array.isArray(providerNumbersInput)
      ? providerNumbersInput
      : [providerNumbersInput];
    dispatch({ type: types.ADD_BOOKMARK_REQUEST });
    try {
      const res = await mutateAsync(providerNumbers);
      if (res.status === 207 || res.status === 201) {
        const results = res?.data?.results || [];
        const successfulBookmarks = results.filter(
          (r) =>
            r.success &&
            r.message === "Bookmark added successfully" &&
            r.data,
        );
        const rawBookmarksData = successfulBookmarks.map((r) => r.data);
        const bookmarkList = successfulBookmarks.map((r) =>
          toBookmarkListItem(r.data),
        );
        dispatch({
          type: types.ADD_BOOKMARK_SUCCESS,
          payload: { rawBookmarksData, bookmarkList },
        });
        queryClient.invalidateQueries({ queryKey: BOOKMARKS_QUERY_KEY });
        return {
          success: true,
          message: `Bookmarked ${successfulBookmarks.length} of ${providerNumbers.length}`,
        };
      }
      throw new Error("Unexpected response from server");
    } catch (error) {
      dispatch({ type: types.ADD_BOOKMARK_FAILURE, payload: error.message });
      return { success: false, message: error.message };
    }
  };
};

// Returns an async fn matching the old thunk contract: resolves to
// { success, data } / { success, message } and mirrors into Redux.
export const useRemoveBookmark = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: (providerNumbers) => removeBookmarkApi(providerNumbers),
  });

  return async (providerNumbers = []) => {
    dispatch({ type: types.REMOVE_BOOKMARK_REQUEST });
    try {
      const numbersArray = Array.isArray(providerNumbers)
        ? providerNumbers
        : [providerNumbers];
      if (!numbersArray.length) {
        throw new Error("No provider numbers provided");
      }
      const res = await mutateAsync(numbersArray);
      if (res.status === 200) {
        const deletedNumbers = res.data.deleted.map(Number);
        dispatch({
          type: types.REMOVE_BOOKMARK_SUCCESS,
          payload: deletedNumbers,
        });
        // When the "All Bookmarks" table is open, drop the removed rows from it
        // immediately (re-numbering the serial column) so the table reflects the
        // change without a page refresh.
        const tableViewData = store.getState()?.app?.tableViewData;
        if (
          tableViewData?.isBookmarkData &&
          Array.isArray(tableViewData?.data)
        ) {
          const deletedSet = new Set(deletedNumbers);
          const remaining = tableViewData.data
            .filter((row) => !deletedSet.has(Number(row.provider_number)))
            .map((row, index) => ({ ...row, "Sr.": index + 1 }));
          if (remaining.length !== tableViewData.data.length) {
            const removedCount = tableViewData.data.length - remaining.length;
            const newTotal = Math.max(
              0,
              (tableViewData.totalCount ?? tableViewData.data.length) -
                removedCount,
            );
            dispatch(
              setHospitalsDetails({ data: remaining, totalCount: newTotal }),
            );
          }
        }
        queryClient.invalidateQueries({ queryKey: BOOKMARKS_QUERY_KEY });
        return { success: true, data: res.data };
      }
      throw new Error("Unexpected response from server");
    } catch (error) {
      dispatch({ type: types.REMOVE_BOOKMARK_FAILURE, payload: error.message });
      return { success: false, message: error.message };
    }
  };
};
